import { useLayoutEffect, useRef, useState } from 'react';

/**
 * The design system flags dark mode by suffixing the theme name on a
 * container's `data-theme` attribute, e.g. `aflm` -> `aflm-dark` (see
 * `afl-design-system/lib/css/theme/*`). Everything the DS themes is a CSS
 * custom property, so components only need to read this when dark mode means a
 * different *asset* rather than a different token — team crests, for instance.
 */
const DARK_THEME_SUFFIX = '-dark';

const subscribers = new Set();

let observer = null;

/**
 * Watches the whole document for `data-theme` being added, changed or removed,
 * and re-runs every subscriber when it happens. One shared observer rather than
 * one per component, since a page can hold a lot of themed crests.
 *
 * The attribute is watched document-wide rather than on a resolved container
 * because it doesn't have to exist when a component mounts.
 *
 * @param {Function} onThemeChange - Called on every `data-theme` mutation.
 * @returns {Function} Unsubscribe, disconnecting the observer once unused.
 */
const subscribeToThemeChanges = (onThemeChange) => {
    subscribers.add(onThemeChange);

    if (!observer) {
        observer = new MutationObserver(() =>
            subscribers.forEach((subscriber) => subscriber())
        );

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme'],
            subtree: true
        });
    }

    return () => {
        subscribers.delete(onThemeChange);

        if (!subscribers.size) {
            observer.disconnect();
            observer = null;
        }
    };
};

/**
 * A custom hook for components that need to know whether they're being
 * rendered inside a dark design system theme.
 *
 * Attach the returned ref to the component's outermost element; the closest
 * `[data-theme]` ancestor (or that element itself) is the one that counts, and
 * it's re-resolved whenever the attribute changes anywhere in the document.
 *
 * @returns {Array} A tuple of the ref to attach, and whether the nearest
 * themed container is currently in dark mode.
 */
export const useIsDarkTheme = () => {
    const ref = useRef(null);
    const [isDarkTheme, setIsDarkTheme] = useState(false);

    // Layout effect rather than useEffect so the correct asset is in place
    // before the browser paints, otherwise dark surfaces flash the light theme.
    useLayoutEffect(() => {
        const readTheme = () => {
            const themedContainer = ref.current?.closest('[data-theme]');

            setIsDarkTheme(
                Boolean(
                    themedContainer?.dataset.theme?.endsWith(DARK_THEME_SUFFIX)
                )
            );
        };

        readTheme();

        return subscribeToThemeChanges(readTheme);
    }, []);

    return [ref, isDarkTheme];
};
